import { DEFAULT_MAX_AMOUNT, DEFAULT_MIN_AMOUNT } from '@config/defaults';
import { Checkout, DONATION_FREQUENCY, LEGAL_PERSON, PAYMENT_METHOD_TYPE } from '@types';
import { t } from 'i18next';
import * as yup from 'yup';

const PHONE_REGEX =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const createFormValuesSchema = (checkout: Checkout) => {
  const reqMsg = t('This field is required');

  return yup
    .object({
      lastStep: yup.number(),

      legalPerson: yup.mixed<LEGAL_PERSON>().oneOf(Object.values(LEGAL_PERSON)).required(reqMsg),

      amount: yup
        .number()
        .when('frequency', {
          is: DONATION_FREQUENCY.ONEOFF,
          then: yup
            .number()
            .required(reqMsg)
            .min(
              checkout.campaign.min_amount || DEFAULT_MIN_AMOUNT,
              t(`The amount must be at least {{amount}}`, {
                amount: (checkout.campaign.min_amount || DEFAULT_MIN_AMOUNT).toString(),
              }),
            )
            .max(
              checkout.campaign.max_amount || DEFAULT_MAX_AMOUNT,
              t('Please contact us for donations of this amount'),
            ),
        })
        .required(reqMsg),
      frequency: yup.mixed<DONATION_FREQUENCY>().required(reqMsg),

      businessName: yup.string().when('legalPerson', {
        is: (value: string) =>
          checkout.campaign.required_fields.includes('business_name') &&
          value == LEGAL_PERSON.COMPANY,
        then: (schema) => schema.required(reqMsg),
      }),
      vat: yup.string().when('legalPerson', {
        is: (value: string) =>
          checkout.campaign.required_fields.includes('vat') && value == LEGAL_PERSON.COMPANY,
        then: (schema) => schema.required(reqMsg),
      }),

      firstName: yup.string().when('legalPerson', {
        is: (value: string) =>
          checkout.campaign.required_fields.includes('first_name') &&
          value == LEGAL_PERSON.INDIVIDUAL,
        then: (schema) => schema.required(reqMsg),
      }),
      lastName: yup.string().when('legalPerson', {
        is: (value: string) =>
          checkout.campaign.required_fields.includes('last_name') &&
          value == LEGAL_PERSON.INDIVIDUAL,
        then: (schema) => schema.required(reqMsg),
      }),
      ssn: yup.string().when('legalPerson', {
        is: (value: string) =>
          checkout.campaign.required_fields.includes('ssn') && value == LEGAL_PERSON.INDIVIDUAL,
        then: (schema) =>
          schema
            .required(reqMsg)
            .matches(/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/, t('Invalid SSN')),
      }),

      email: checkout.campaign.required_fields.includes('email')
        ? yup.string().email(t('Insert a valid email')).required(reqMsg)
        : yup.string().email(t('Insert a valid email')),
      phone: checkout.campaign.required_fields.includes('phone')
        ? yup
            .string()
            .matches(PHONE_REGEX, {
              message: t('Insert a valid phone number'),
              excludeEmptyString: true,
            })
            .required(reqMsg)
        : yup.string().matches(PHONE_REGEX, {
            message: t('Insert a valid phone number'),
            excludeEmptyString: true,
          }),
      mobile: checkout.campaign.required_fields.includes('mobile')
        ? yup
            .string()
            .matches(PHONE_REGEX, {
              message: t('Insert a valid phone number'),
              excludeEmptyString: true,
            })
            .required(reqMsg)
        : yup.string().matches(PHONE_REGEX, {
            message: t('Insert a valid phone number'),
            excludeEmptyString: true,
          }),
      address: checkout.campaign.required_fields.includes('address')
        ? yup.string().required(reqMsg)
        : yup.string(),
      city: checkout.campaign.required_fields.includes('city')
        ? yup.string().required(reqMsg)
        : yup.string(),
      locality: checkout.campaign.required_fields.includes('locality')
        ? yup.string().required(reqMsg)
        : yup.string(),
      postalCode: checkout.campaign.required_fields.includes('postal_code')
        ? yup.string().required(reqMsg)
        : yup.string(),
      country: checkout.campaign.required_fields.includes('country')
        ? yup.string().required(reqMsg)
        : yup.string(),

      paymentMethod: yup
        .mixed<PAYMENT_METHOD_TYPE>()
        .oneOf(Object.values(PAYMENT_METHOD_TYPE))
        .required(reqMsg),

      paymentMethodId: yup.number(),
    })
    .required();
};

export default createFormValuesSchema;
